import React, { useCallback, useMemo, useState } from 'react';
import useAuditBatch from 'react-scale/components/audits/SpeedAudit/v2/hooks/useAuditBatch';
import QMLoadingPage from 'react-scale/corp/components/QM/QMLoadingPage';
import QMErrorPage from 'react-scale/corp/components/QM/QMErrorPage';
import { Alert, Button, Checkbox, Chip, Tab, Tabs } from 'design-system';
import { IAttemptAuditJson } from '@scale/shared/types/speedAudit';
import Table from 'react-scale/corp/components/UI/Table';
import ObjectIDFormatter from 'react-scale/corp/components/Format/ObjectIDFormatter';
import {
  resultColors,
  resultV2Colors,
  AttemptAuditResultTableRow,
  SubtaskAssignment,
} from 'react-scale/components/audits/SpeedAudit/types';
import { AuditGradeV2, AuditResultV2, AuditResultV2Collection } from '@scale/shared/defaults';
import Heatmap, { IHeatmapItem } from 'react-scale/components/audits/SpeedAudit/heatmap';
import { getColorAssignments } from 'react-scale/components/audits/SpeedAudit/colorOfTags';
import _ from 'lodash';
import IsomorphicLink from 'react-scale/corp/components/UI/IsomorphicLink';
import save from 'save-file';
import { arrayToCSV } from 'react-scale/corp/components/UI/DataUpload/arrayToCSV';
import { PartialRecord } from '@scale/shared/types/utils';
import { getLatestResponseFromSubtaskAssignments } from 'react-scale/components/audits/SpeedAudit/utils';

interface SpeedAuditResultsProps {
  auditBatchId: string;
  // used to augment view results if present
  subtaskAssignments?: PartialRecord<string, SubtaskAssignment>;
}

const colorOfResult = (value?: any) => resultColors[value as keyof typeof resultColors] ?? 'white';
const colorOfResultV2 = (value?: any) =>
  resultV2Colors[value as keyof typeof resultV2Colors] ?? 'white';

const ResultFormatter = ({ value }: { value: string }) => (
  <>
    {value && (
      <div
        style={{
          padding: '0 5px',
          borderLeft: '5px solid ' + resultColors[value as keyof typeof resultColors],
        }}
      >
        {value}
      </div>
    )}
    {!value && <span style={{ color: 'gray' }}>Not graded</span>}
  </>
);
const ResultV2Formatter = ({ value }: { value: AuditResultV2Collection }) => (
  <div>
    {!!value?.length &&
      value.map((v, i) => (
        <div
          key={i}
          style={{
            padding: '0 5px',
            borderLeft: '5px solid ' + colorOfResultV2(v.result),
          }}
        >
          {v.result}
        </div>
      ))}
    {!value?.length && <span style={{ color: 'gray' }}>Not graded</span>}
  </div>
);

const ErrorsFormatter = ({ value }: { value: AuditResultV2Collection }) => {
  // Flatten all the errors to a single list
  const errors = _.uniq(value.flatMap(grade => grade.errorCategories));
  return (
    <>
      {errors.map(e => (
        <Chip key={e} label={e} />
      ))}
    </>
  );
};

const AttemptAuditTable: React.FC<{
  attemptAudits: AttemptAuditResultTableRow[];
  subtaskAssignments?: PartialRecord<string, SubtaskAssignment>;
}> = ({ attemptAudits, subtaskAssignments }) => {
  const hasOldResult = attemptAudits && attemptAudits.find(a => !!a.result);
  const hasNewResult = attemptAudits && attemptAudits.find(a => a.resultV2?.length);

  const columns: $FIXME[] = [
    {
      id: 'subtask',
      accessor: (row: AttemptAuditResultTableRow) => {
        if (row.subtask) {
          return row.subtask;
        } else {
          return row.task ?? '';
        }
      },
      Header: 'Subtask',
      width: 200,
      filterable: true,
      Cell: ObjectIDFormatter,
    },
    {
      id: 'lastAttempt',
      accessor: (row: AttemptAuditResultTableRow) => {
        if (row.lastAttempt) {
          return row.lastAttempt;
        } else if (subtaskAssignments) {
          return getLatestResponseFromSubtaskAssignments(subtaskAssignments, row.task)?.id ?? '';
        } else {
          return '';
        }
      },
      Header: 'Latest TaskAttempt',
      width: 200,
      filterable: true,
      Cell: ObjectIDFormatter,
    },
    {
      id: 'lastAttempter.email',
      accessor: (row: AttemptAuditResultTableRow) => {
        if (row.lastAttempter?.email) {
          return row.lastAttempter.email;
        } else if (subtaskAssignments) {
          return (
            getLatestResponseFromSubtaskAssignments(subtaskAssignments, row.task)?.attemptedBy ?? ''
          );
        } else {
          return '';
        }
      },
      Header: 'Latest Attempter',
      width: 200,
      filterable: true,
      Cell: ObjectIDFormatter,
    },
  ];

  if (hasNewResult) {
    columns.push({
      accessor: 'resultV2',
      Header: 'Result',
      width: 175,
      filterable: true,
      Cell: ResultV2Formatter,
      filterMethod: ({ value }: { value: string }, r: any) => {
        return (
          !value ||
          (r.resultV2?.length &&
            r.resultV2
              .map(({ result }: AuditGradeV2) => result)
              .map((e: AuditResultV2) => e.toLowerCase())
              .filter((e: string) => e.includes(value.toLowerCase())).length)
        );
      },
    });
    columns.push({
      accessor: 'resultV2',
      Header: 'Errors',
      sortable: false,
      Cell: ErrorsFormatter,
      width: 150,
      filterable: true,
      filterMethod: ({ value }: { value: string }, r: any) => {
        return (
          !value ||
          (r.resultV2?.length &&
            r.resultV2
              .flatMap(({ errorCategories }: AuditGradeV2) => errorCategories)
              .map((e: string) => e.toLowerCase())
              .filter((e: string) => e.includes(value.toLowerCase())).length)
        );
      },
    });
  }
  if (hasOldResult) {
    columns.push({
      accessor: 'result',
      Header: 'Result',
      width: 175,
      filterable: true,
      Cell: ResultFormatter,
      filterMethod: ({ value }: { value: string }, r: any) => {
        return !value || r.result?.toLowerCase().includes(value.toLowerCase());
      },
    });
  }

  columns.push({
    accessor: 'auditBatch',
    Header: 'Audit Batch',
    width: 200,
    filterable: true,
    Cell: ObjectIDFormatter,
  });

  columns.push({
    accessor: 'task',
    Header: 'Task',
    width: 200,
    filterable: true,
    Cell: ObjectIDFormatter,
  });
  columns.push({
    accessor: 'type',
    Header: 'Type',
    width: 200,
    filterable: true,
  });

  return (
    <Table
      defaultPageSize={10}
      style={{ marginTop: '10px' }}
      columns={columns}
      data={attemptAudits}
    />
  );
};

const SpeedAuditResultsV2: React.FC<SpeedAuditResultsProps> = ({
  auditBatchId,
  subtaskAssignments,
}) => {
  const { value, loading, error } = useAuditBatch(auditBatchId);
  const [includeCommentsInCsv, setIncludeCommentsInCsv] = useState(false);

  const errorTypeColors: Record<string, string> = useMemo(() => {
    const defaultErrorTypes: Record<string, string> = {};
    if (!value?.attemptAudits) return defaultErrorTypes;
    const errorTypes = (value.attemptAudits as unknown as IAttemptAuditJson[])
      .flatMap(aa => aa.resultV2?.map(({ errorCategories }) => errorCategories) ?? [])
      .flat();
    const byCount = _.countBy(errorTypes) as Record<string, number>;
    const sorted = _.entries(byCount)
      .sort(([, c1]: [string, number], [, c2]: [string, number]) => c2 - c1)
      .map(([e]: [string, number]) => e);
    return getColorAssignments(sorted);
  }, [value]);

  const colorOfError = (value?: any) =>
    errorTypeColors[value as keyof typeof errorTypeColors] ?? 'white';
  const getResultsHeatmap = (auditAttempts: IAttemptAuditJson[]): IHeatmapItem[] => {
    return auditAttempts.map(attempt => ({
      value: [attempt.result],
      tooltip: `${attempt.auditLookup.id}: ${attempt.result ?? 'Not graded'} `,
      href: `/lookup/${attempt.auditLookup.id}`,
    }));
  };
  const getErrorsHeatmap = (auditAttempts: IAttemptAuditJson[]): IHeatmapItem[] => {
    return auditAttempts.map(attempt => {
      const errorTypes = attempt.resultV2?.flatMap(({ errorCategories }) => errorCategories) ?? [];
      return {
        value: errorTypes,
        tooltip: `${attempt.auditLookup.id}: ${errorTypes?.join(', ') ?? 'Not graded'} `,
        href: `/lookup/${attempt.auditLookup.id}`,
      };
    });
  };
  const getResultsV2Heatmap = (auditAttempts: IAttemptAuditJson[]): IHeatmapItem[] => {
    return auditAttempts.map(attempt => {
      const results = _.uniq(attempt.resultV2?.map(({ result }) => result) ?? []);
      return {
        value: results,
        tooltip: `${attempt.auditLookup.id}: ${results?.join(', ') ?? 'Not graded'} `,
        href: `/lookup/${attempt.auditLookup.id}`,
      };
    });
  };

  const csvData = useMemo(() => {
    if (!value?.attemptsWithAttempters) return [];
    const data = value.attemptsWithAttempters as unknown as {
      auditLookup: {
        id: string;
      };
      auditedBy: string;
      subtask: string;
      task: string;
      lastAttempt: string;
      type: string;
      auditBatch: string;
      lastAttempter: Record<string, any>;
      project: string;
      result: string;
      resultV2: AuditResultV2Collection;
    }[];

    return arrayToCSV(
      data.map(
        ({
          subtask,
          lastAttempt,
          lastAttempter,
          auditedBy,
          project,
          auditBatch,
          task,
          type,
          result,
          resultV2,
        }) => ({
          subtask,
          lastAttempt,
          lastAttempter: lastAttempter?.email,
          auditedBy,
          project,
          auditBatch,
          task,
          type,
          result,
          result2: resultV2?.map(x => x.result).join('; '),
          errorCategories: _.uniq(resultV2.flatMap(grade => grade.errorCategories)).join('; '),
          ...(includeCommentsInCsv
            ? {
                feedback: resultV2
                  ?.map(grade => grade.feedback)
                  .filter(Boolean)
                  .join('; '),
              }
            : {}),
        }),
      ),
    );
  }, [value?.attemptsWithAttempters, includeCommentsInCsv]);

  const downloadCSV = useCallback(async () => {
    const name = value?.auditBatch.name ?? 'speed audit';
    await save(csvData, name + '.csv');
  }, [csvData, value]);

  return (
    <div
      style={{
        padding: '10px',
      }}
    >
      {loading && (
        <div
          style={{
            minWidth: '400px',
            minHeight: '400px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          {' '}
          <QMLoadingPage />
          <div style={{ display: 'flex', justifyContent: 'center' }}>Loading audit summary</div>
        </div>
      )}
      {error && <QMErrorPage error={error} />}
      {value && (
        <>
          <h2 style={{ display: 'flex', alignItems: 'center' }}>
            {value.auditBatch.name} on {new Date(value.auditBatch.createdAt).toLocaleDateString()}
            <div style={{ flexGrow: 1, minWidth: '100px' }} />
            <IsomorphicLink href={`/v2/speed_audit/${value.auditBatch._id}`} target={'_blank'}>
              <Button>Open</Button>
            </IsomorphicLink>
            <Checkbox
              label="Include comments in CSV"
              checked={includeCommentsInCsv}
              onChange={e => setIncludeCommentsInCsv(e.target.checked)}
              style={{ marginLeft: 12 }}
            />
            <Button type={'primary'} style={{ marginLeft: '10px' }} onClick={downloadCSV}>
              CSV
            </Button>
          </h2>
          <Tabs>
            <Tab label="Results">
              <Heatmap
                items={getResultsHeatmap(value.attemptAudits as unknown as IAttemptAuditJson[])}
                colorOfValue={colorOfResult}
              />
            </Tab>
            <Tab label="Results (beta)">
              <Heatmap
                items={getResultsV2Heatmap(value.attemptAudits as unknown as IAttemptAuditJson[])}
                colorOfValue={colorOfResultV2}
              />
              <Alert severity="info">
                Results for speed audits 2.0 have a different format. We will consolidate the two
                formats once there is more data.
              </Alert>
            </Tab>
            <Tab label="Error Categories">
              <Heatmap
                items={getErrorsHeatmap(value.attemptAudits as unknown as IAttemptAuditJson[])}
                colorOfValue={colorOfError}
              />
            </Tab>
          </Tabs>
          <AttemptAuditTable
            attemptAudits={value.attemptsWithAttempters as AttemptAuditResultTableRow[]}
            subtaskAssignments={subtaskAssignments}
          />{' '}
        </>
      )}
    </div>
  );
};
export default React.memo(SpeedAuditResultsV2);
